import {
  BOOLEAN_OPERATORS,
  DATE_OPERATORS,
  ENUM_OPERATORS,
  QueryFieldDefinition,
  TEXT_OPERATORS,
} from './types'

/** Queryable fields for the TICKET module. */
export const TICKET_QUERY_FIELDS: QueryFieldDefinition[] = [
  {
    key: 'text',
    label: 'Text',
    type: 'text',
    description:
      'Searches subject, description, ticket number and requester name/email',
    example: 'text ~ "billing issue"',
    operators: ['~', '!~'],
  },
  {
    key: 'subject',
    aliases: ['name', 'title', 'summary'],
    label: 'Subject',
    type: 'text',
    example: 'subject ~ "refund"',
    operators: TEXT_OPERATORS,
    sortable: true,
  },
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    operators: ['~', '!~', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'key',
    aliases: ['ticketNumber', 'number'],
    label: 'Ticket number',
    type: 'text',
    example: 'key = TICK-42',
    operators: ['=', '!=', '~', 'IN', 'NOT IN'],
  },
  {
    key: 'stage',
    aliases: ['status'],
    label: 'Stage',
    type: 'enum',
    description: 'Matches stage name or id (e.g. "In Review", new)',
    example: 'stage = "In Review"',
    operators: ENUM_OPERATORS,
    optionSource: 'statuses',
    sortable: true,
  },
  {
    key: 'stageType',
    aliases: ['statusType'],
    label: 'Stage type',
    type: 'enum',
    description: 'Semantic stage category (e.g. NEW, RESOLVED)',
    example: 'stageType != RESOLVED',
    operators: ENUM_OPERATORS,
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'enum',
    example: 'priority IN (HIGH, URGENT)',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
      { value: 'URGENT', label: 'Urgent' },
    ],
    sortable: true,
  },
  {
    key: 'source',
    label: 'Source',
    type: 'enum',
    example: 'source = EMAIL',
    operators: ENUM_OPERATORS,
    enumValues: [
      { value: 'INTERNAL', label: 'Internal' },
      { value: 'FORM', label: 'Form' },
      { value: 'PORTAL', label: 'Portal' },
      { value: 'EMAIL', label: 'Email' },
      { value: 'WORKFLOW', label: 'Workflow' },
      { value: 'IMPORT', label: 'Import' },
    ],
  },
  {
    key: 'type',
    aliases: ['ticketType'],
    label: 'Ticket type',
    type: 'reference',
    description: 'Matches ticket type key or name',
    operators: ENUM_OPERATORS,
    optionSource: 'taskTypes',
  },
  {
    key: 'assignee',
    label: 'Assignee',
    type: 'user',
    description: 'Matches by name or email; currentUser() supported',
    example: 'assignee = currentUser()',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'requester',
    label: 'Requester',
    type: 'text',
    description: 'Matches requester name or email',
    example: 'requester ~ "acme.com"',
    operators: ['=', '!=', '~', '!~', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'creator',
    aliases: ['createdBy'],
    label: 'Creator',
    type: 'user',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
    optionSource: 'members',
    supportsCurrentUser: true,
  },
  {
    key: 'tag',
    aliases: ['tags'],
    label: 'Tag',
    type: 'enum',
    description: 'Matches tickets carrying the tag',
    example: 'tag = vip',
    operators: ['=', '!=', 'IN', 'NOT IN', 'IS EMPTY', 'IS NOT EMPTY'],
  },
  {
    key: 'due',
    aliases: ['dueDate'],
    label: 'Due date',
    type: 'date',
    example: 'due <= endOfWeek()',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'slaDue',
    aliases: ['slaDueAt'],
    label: 'SLA due',
    type: 'date',
    example: 'slaDue <= now()',
    operators: DATE_OPERATORS,
  },
  {
    key: 'created',
    aliases: ['createdAt'],
    label: 'Created date',
    type: 'date',
    example: 'created >= -30d',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'updated',
    aliases: ['updatedAt'],
    label: 'Updated date',
    type: 'date',
    operators: DATE_OPERATORS,
    sortable: true,
  },
  {
    key: 'resolved',
    aliases: ['resolvedAt'],
    label: 'Resolved date',
    type: 'date',
    example: 'resolved >= startOfMonth()',
    operators: DATE_OPERATORS,
  },
  {
    key: 'closed',
    aliases: ['closedAt'],
    label: 'Closed date',
    type: 'date',
    operators: DATE_OPERATORS,
  },
  {
    key: 'isPrivate',
    aliases: ['private'],
    label: 'Is private',
    type: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
]
